/**
 * Survey page — stores a logged-in customer's survey answers as custom
 * properties on their Klaviyo profile.
 *
 * WHY THIS IS A GLOBAL ASSET AND NOT INLINE IN THE SECTION
 * The customer-dashboard app (/apps/customerdashboard?page=survey) injects the
 * section's markup into its own tab, and a <script> inside injected HTML never
 * executes — so an inline script would silently do nothing there and the page
 * would sit on its loading state forever. Loading it from theme.liquid instead
 * means the <survey-page> definition already exists when the app injects the
 * markup, and the element upgrades on insertion. Same reason wishlist-page.js
 * and waitlist-page.js are global. The section's CSS stays inline in the
 * section, because stylesheets DO apply to injected markup.
 *
 * The script self-guards: it only defines a custom element, so it does nothing
 * at all on pages that don't contain <survey-page>.
 *
 * READ (needs the private key, so it goes through the proxy):
 *   GET {profilesEndpoint}   (no filter: the route pins it on the server)
 *     -> SecureBite -> Klaviyo GET /api/profiles/?filter=equals(email,"...")
 *        with the logged-in customer's email, which SecureBite looks up from
 *        Shopify itself, and fields[profile]=properties
 *     -> data[0].attributes.properties = every custom property on the profile
 *   The key stays server-side, and the browser can't ask for anyone else.
 *   No match (data: []) is not an error — it just means Klaviyo has never seen
 *   this email, and the first save will create the profile.
 *
 * WRITE (public key, safe in the browser):
 *   POST https://a.klaviyo.com/client/profiles/?company_id=<public key>
 *   An UPSERT keyed on the email: it creates the profile when Klaviyo doesn't
 *   have one and updates it when it does, so "check if the user exists, create
 *   them if not" is a single call. Properties sent in attributes.properties are
 *   merged into the profile, not swapped for it.
 *
 *   Klaviyo does NOT require an existing subscription for this. Profiles and
 *   consent are separate records: creating a profile grants no marketing
 *   consent, which can only be granted through the subscribe endpoints (never
 *   called here). Profiles created this way do still count toward the store's
 *   active profile total, which is what Klaviyo bills on.
 *
 * REQUIRED
 * Every question must be answered before anything is saved. Pressing Save early
 * saves nothing, highlights the unanswered questions and focuses the first one.
 * Nothing is disabled up front — a dead Save button doesn't say why.
 *
 * STALE ANSWERS
 * Questions come from the shared "Survey question" metaobjects, each naming
 * its Klaviyo property, so rewording a question keeps its answers. An answer
 * no longer among the options doesn't match a checkbox and shows empty until
 * the customer saves again, as intended. On save we also unset any property
 * under our prefix that no longer belongs to a question.
 *
 * Data attributes (set by sections/main-survey.liquid):
 *   data-profiles-endpoint  SecureBite route for Get Profiles
 *   data-email              customer.email
 *   data-customer-id        customer.id, sent to Klaviyo as external_id
 *   data-public-key         Klaviyo public API key (company id)
 *   data-api-revision       Klaviyo API revision header
 *   data-property-prefix    prefix owned by this survey, e.g. "Survey: "
 *   data-preview            "true" in the theme editor, where there is no
 *                           customer. The form renders blank and Save is a
 *                           no-op, so laying out questions can't write to a
 *                           real Klaviyo profile.
 *   data-saving-text / data-success-text / data-save-error-text  status copy
 *
 * States: loading -> (form | error | signin). The form stays hidden when the
 * read fails, so a save can't overwrite real answers with blanks.
 *
 * PROFILE LINK (public landing page, e.g. from a Klaviyo email)
 * With data-profile-param set (the section's "Allow Klaviyo profile link"),
 * a visitor who is not signed in can still answer, identified by the Klaviyo
 * profile id in the URL: ?p={{ person.KlaviyoID }}. The id is not personal
 * data and cannot be worked out from knowing someone, unlike an email.
 *   read  — GET {linkEndpoint}?p=<id>, a SecureBite route that takes the id
 *           and nothing else: it validates the id, calls Klaviyo's
 *           single-profile endpoint itself and returns only the properties.
 *           It cannot look anyone up by email or list profiles, which is why
 *           it can stay open to visitors who aren't logged in while
 *           {profilesEndpoint} requires a login. 404 means a bad or stale
 *           link: show the sign-in state.
 *   write — the same client upsert, identified by data.id alone. No email is
 *           read or sent: the id pins the exact profile on its own.
 * A valid ?p= wins over a signed-in customer: the link names the profile the
 * email was sent to, which is the one marketing's segments look at.
 * Do not use klaviyo.js identify() for this — the cookie banner keeps it
 * dormant until consent, and it writes nothing in the meantime.
 *
 * LEAST DATA
 * Lookups return the profile's properties only — no email, name, phone or
 * location — because nothing here needs them. SecureBite enforces this on
 * both routes (a secret fields[profile]=properties, plus a properties-only
 * response on the link route); the email lookup also asks for it, so the
 * page keeps working whichever side changes.
 *
 * FRESH READS AFTER A SAVE
 * SecureBite can cache the two reads above (the route's "Cache responses"
 * setting). The save goes straight to Klaviyo, so SecureBite never sees it,
 * and Klaviyo applies it a few seconds after answering 202. So after a save:
 *   - reads add _fresh=1 for FRESH_WINDOW_MS. That skips SecureBite's stored
 *     copy and replaces it with what Klaviyo returns.
 *   - two delayed background reads with _fresh=1 refresh the stored copy once
 *     Klaviyo has caught up, so later normal reads get the new answers.
 * The window lives in localStorage (FRESH_KEY) so survey-prompt.js, which
 * reads the same URL, honours it too. _fresh is harmless with caching off:
 * SecureBite drops it before calling Klaviyo.
 *
 * FLAG PROPERTIES
 * A question may also mirror each answer onto its own property
 * (data-flags = {"<answer>": "<property>"}), e.g. "Flexible Fit" ->
 * "Curation: Flexible Fit" = "Yes", so existing segments keep working off one
 * record. Ticked -> "Yes", unticked -> unset. On read the two sources are
 * merged: an answer counts as ticked if either the list or its flag says so.
 *
 * CARD LAYOUT (the email landing page)
 *   data-preselect-param  "c": ?c=flexible-fit (answer handle, or the answer
 *                         itself; comma-separate several) ticks those answers
 *                         on top of the stored ones. Nothing saves until Save.
 *   data-owned-keys       JSON list of every question's key, so a page that
 *                         shows only some questions doesn't unset the others.
 *   data-explore-label    "Explore [category]" for the button after a save.
 * After a save the form gives way to [data-survey-saved]. Its Explore button
 * follows the first answer ticked on this visit (falling back to the email's
 * pre-ticked one, then the first ticked card), using the card's data-label
 * and data-link.
 */
if (!customElements.get('survey-page')) {
  // Shared with survey-prompt.js. See FRESH READS AFTER A SAVE above.
  const FRESH_KEY = 'securebite-fresh-until';
  const FRESH_WINDOW_MS = 10 * 60 * 1000;
  const FRESH_REFRESH_DELAYS = [4000, 15000];

  customElements.define(
    'survey-page',
    class SurveyPage extends HTMLElement {
      connectedCallback() {
        // connectedCallback runs again if the element is moved in the DOM,
        // and re-binding submit would save twice per click. Bind once.
        if (this.initialized) return;
        this.initialized = true;

        this.profilesEndpoint = this.dataset.profilesEndpoint || '/apps/proxy/klaviyo-profile';
        this.linkEndpoint = this.dataset.linkEndpoint || '/apps/proxy/klaviyo-preferences';
        this.email = this.dataset.email || '';
        this.customerId = this.dataset.customerId || '';
        this.publicKey = this.dataset.publicKey || '';
        this.apiRevision = this.dataset.apiRevision || '2024-10-15';
        // NOT this.prefix — Element.prefix is a read-only DOM property
        // (the XML namespace prefix) and assigning to it throws in the
        // strict mode a class body always runs in, which would kill
        // connectedCallback and leave the spinner up forever.
        this.propertyPrefix = this.dataset.propertyPrefix || '';
        this.preview = this.dataset.preview === 'true';
        this.profileId = this.readProfileParam();

        this.form = this.querySelector('[data-survey-form]');
        this.loadingEl = this.querySelector('[data-survey-loading]');
        this.errorEl = this.querySelector('[data-survey-error]');
        this.signinEl = this.querySelector('[data-survey-signin]');
        this.messageEl = this.querySelector('[data-survey-message]');
        this.saveButton = this.querySelector('[data-survey-save]');
        this.resetButton = this.querySelector('[data-survey-reset]');
        this.questions = Array.from(this.querySelectorAll('[data-survey-question]'));

        // Keys the survey currently owns — anything else under our prefix
        // came from a question that has since changed or been removed.
        // data-owned-keys lists every question's key, including questions this
        // page doesn't show, so their answers aren't mistaken for stale ones.
        this.currentKeys = new Set(
          this.questions.map(function (q) { return q.dataset.property; }).filter(Boolean)
        );
        try {
          for (const key of JSON.parse(this.dataset.ownedKeys || '[]')) this.currentKeys.add(key);
        } catch (e) {
          console.error('[survey] bad data-owned-keys', e);
        }
        this.staleKeys = [];

        // Card layout: the saved panel and the order answers were ticked in,
        // which picks the category the Explore button points at.
        this.savedEl = this.querySelector('[data-survey-saved]');
        this.exploreEl = this.querySelector('[data-survey-explore]');
        this.changeButton = this.querySelector('[data-survey-change]');
        this.picked = [];
        this.preselected = [];

        if (this.form) {
          this.form.addEventListener('submit', this.onSubmit.bind(this));
          this.form.addEventListener('change', this.onChange.bind(this));
        }
        if (this.resetButton) this.resetButton.addEventListener('click', this.onReset.bind(this));
        if (this.changeButton) this.changeButton.addEventListener('click', this.onChangeChoices.bind(this));

        this.load();
      }

      show(el, visible) {
        if (el) el.hidden = !visible;
      }

      setState(state) {
        this.show(this.loadingEl, state === 'loading');
        this.show(this.errorEl, state === 'error');
        this.show(this.form, state === 'form');
        this.show(this.signinEl, state === 'signin');
        this.show(this.savedEl, state === 'saved');
      }

      // Ticks the answers named in the email link (?c=flexible-fit, or several
      // comma-separated), on top of whatever the profile already has. Nothing
      // is saved until they press Save. Matched by handle, or by the answer
      // itself, so &c=Flexible%20Fit works too.
      applyPreselect() {
        const param = this.dataset.preselectParam;
        if (!param || this.preview) return;
        let raw = '';
        try {
          raw = new URLSearchParams(window.location.search).get(param) || '';
        } catch (e) {
          return;
        }
        const wanted = raw.split(',').map(function (v) { return v.trim().toLowerCase(); }).filter(Boolean);
        if (!wanted.length) return;

        for (const question of this.questions) {
          const inputs = Array.from(question.querySelectorAll('.survey__checkbox'));
          for (const input of inputs) {
            const handle = (input.dataset.handle || '').toLowerCase();
            if (wanted.indexOf(handle) === -1 && wanted.indexOf(input.value.toLowerCase()) === -1) continue;
            // A single-answer question can only take one; the link wins.
            if (question.dataset.multiple !== 'true') {
              for (const other of inputs) other.checked = false;
            }
            input.checked = true;
            this.preselected.push(input);
          }
        }
      }

      // The category the Explore button points at: the first answer ticked on
      // this visit, then the one the email link pre-ticked, then the first
      // ticked card. Only cards carry a link, so the list layout never has one.
      exploreTarget() {
        const ticked = function (input) { return input.checked && input.dataset.link; };
        return (
          this.picked.find(ticked) ||
          this.preselected.find(ticked) ||
          Array.from(this.querySelectorAll('.survey__checkbox')).find(ticked) ||
          null
        );
      }

      showSaved() {
        const target = this.exploreTarget();
        if (this.exploreEl) {
          if (target) {
            const template = this.dataset.exploreLabel || 'Explore [category]';
            this.exploreEl.textContent = template.replace('[category]', target.dataset.label || target.value);
            this.exploreEl.href = target.dataset.link;
          }
          this.exploreEl.hidden = !target;
        }
        this.setMessage('');
        this.setState('saved');
        // Focus moves to the panel so screen readers announce the confirmation,
        // and the visitor isn't left scrolled down past it on a phone.
        this.savedEl.focus({ preventScroll: true });
        this.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }

      onChangeChoices() {
        this.setState('form');
        const first = this.querySelector('.survey__checkbox');
        if (first) first.focus();
      }

      // Klaviyo profile id from the URL, or '' when the section doesn't allow
      // profile links or the value isn't a plausible id. The format check also
      // keeps anything but letters and digits out of the Klaviyo filter string.
      readProfileParam() {
        const param = this.dataset.profileParam;
        if (!param || this.preview) return '';
        let value = '';
        try {
          value = new URLSearchParams(window.location.search).get(param) || '';
        } catch (e) {
          return '';
        }
        value = value.trim();
        return /^[A-Za-z0-9]{10,64}$/.test(value) ? value : '';
      }

      // { "<answer>": "<property>" } from data-flags, or {} when absent.
      flagsFor(question) {
        if (!question.dataset.flags) return {};
        try {
          return JSON.parse(question.dataset.flags) || {};
        } catch (e) {
          console.error('[survey] bad data-flags', e);
          return {};
        }
      }

      setMessage(text, kind) {
        if (!this.messageEl) return;
        if (!text) {
          this.messageEl.textContent = '';
          this.messageEl.hidden = true;
          return;
        }
        this.messageEl.textContent = text;
        this.messageEl.hidden = false;
        this.messageEl.classList.toggle('survey__message--error', kind === 'error');
        this.messageEl.classList.toggle('survey__message--success', kind === 'success');
      }

      withParams(endpoint, params) {
        const join = endpoint.indexOf('?') === -1 ? '?' : '&';
        return endpoint + join + new URLSearchParams(params).toString();
      }

      // Read params for the current identity: the profile link when there is
      // one, otherwise the email lookup. null when there is nothing to read.
      // fresh: add _fresh=1 (always, or only inside the post-save window).
      readUrl(fresh) {
        let endpoint;
        let params;
        if (this.profileId) {
          endpoint = this.linkEndpoint;
          params = { p: this.profileId };
        } else if (this.email) {
          endpoint = this.profilesEndpoint;
          // The route filters by the logged-in customer's email itself.
          params = {};
        } else {
          return null;
        }
        if (fresh === true || (fresh !== false && this.inFreshWindow())) params._fresh = '1';
        return this.withParams(endpoint, params);
      }

      inFreshWindow() {
        try {
          return Number(localStorage.getItem(FRESH_KEY)) > Date.now();
        } catch (e) {
          return false;
        }
      }

      // After a save: open the fresh window and refresh SecureBite's stored
      // copy once Klaviyo has had time to apply the change.
      afterSave() {
        try {
          localStorage.setItem(FRESH_KEY, String(Date.now() + FRESH_WINDOW_MS));
        } catch (e) {
          /* storage blocked: the background refreshes below still run */
        }
        const url = this.readUrl(true);
        if (!url) return;
        for (const delay of FRESH_REFRESH_DELAYS) {
          setTimeout(function () {
            fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).catch(function () {});
          }, delay);
        }
      }

      /* ------------------------------------------------------------ read */

      async load() {
        this.setState('loading');
        // Profile links are on but there is neither a usable id nor a
        // signed-in customer: nobody to save for.
        if (this.dataset.profileParam && !this.profileId && !this.email && !this.preview) {
          this.setState('signin');
          return;
        }
        try {
          const properties = await this.fetchProperties();
          if (properties === null) {
            this.setState('signin');
            return;
          }
          this.applyProperties(properties);
          this.applyPreselect();
          this.setState('form');
        } catch (e) {
          console.error('[survey]', e);
          this.setState('error');
        }
      }

      // Resolves to the profile's properties, {} when there is nothing to
      // read yet, or null when a profile link matches no profile.
      async fetchProperties() {
        if (this.profileId) return this.fetchLinkProperties();
        if (!this.email) return {};

        const url = this.readUrl();
        const res = await fetch(url, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        });
        if (!res.ok) throw new Error(url + ' -> ' + res.status);

        const body = await res.json();
        const profile = body && Array.isArray(body.data) ? body.data[0] : null;

        // No profile yet is a normal first-visit state, not a failure — the
        // form opens blank and the first save creates the profile.
        return (profile && profile.attributes && profile.attributes.properties) || {};
      }

      // Profile link: id in, properties out. Resolves to null when the id
      // names no profile (404), so the page shows the sign-in state.
      async fetchLinkProperties() {
        const url = this.readUrl();
        const res = await fetch(url, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        });
        if (res.status === 404) return null;
        if (!res.ok) throw new Error(url + ' -> ' + res.status);

        // Saved by id alone, so a signed-in customer's own email and Shopify
        // id must not ride along and point at a different profile.
        this.email = '';
        this.customerId = '';

        const body = await res.json();
        // The route returns { properties: {…} }; an empty or missing object
        // just means nothing has been answered yet.
        return (body && body.properties) || {};
      }

      // Tick the checkboxes whose value matches what Klaviyo has stored.
      // Values that no longer match an option are silently skipped, which
      // is what makes a changed answer list read as unanswered.
      applyProperties(properties) {
        for (const question of this.questions) {
          const key = question.dataset.property;
          const values = this.toValueSet(key ? properties[key] : undefined);
          const flags = this.flagsFor(question);
          for (const input of question.querySelectorAll('.survey__checkbox')) {
            const flag = flags[input.value];
            input.checked = values.has(input.value) || (!!flag && properties[flag] === 'Yes');
          }
        }

        // Only meaningful with a prefix to scope by — without one we'd
        // treat every unrelated profile property as ours and delete it.
        const self = this;
        this.staleKeys = this.propertyPrefix
          ? Object.keys(properties).filter(function (key) {
              return key.indexOf(self.propertyPrefix) === 0 && !self.currentKeys.has(key);
            })
          : [];
      }

      // Klaviyo gives back a string for single-answer questions and an
      // array for multi-answer ones; normalise both to a set of strings.
      toValueSet(stored) {
        if (Array.isArray(stored)) return new Set(stored.map(String));
        if (stored === null || stored === undefined || stored === '') return new Set();
        return new Set([String(stored)]);
      }

      /* ------------------------------------------------------------ edit */

      // Single-answer questions use checkboxes for the look but behave
      // like radios: picking one clears the rest. (Real radios can't be
      // unticked, and clearing an answer has to stay possible.)
      onChange(event) {
        const input = event.target;
        if (!input.classList || !input.classList.contains('survey__checkbox')) return;

        const question = input.closest('[data-survey-question]');
        if (question && question.dataset.multiple !== 'true' && input.checked) {
          for (const other of question.querySelectorAll('.survey__checkbox')) {
            if (other !== input) other.checked = false;
          }
        }
        // Drop the "unanswered" flag as soon as this question has an answer, so
        // the highlight clears while they work instead of only on the next save.
        if (question && this.isAnswered(question)) this.flagMissing(question, false);
        this.setMessage('');

        // Remember the order of picks for the Explore button.
        this.picked = this.picked.filter(function (other) { return other !== input; });
        if (input.checked) this.picked.push(input);
      }

      // Clears every answer outright rather than restoring the last saved set.
      // Nothing is written to Klaviyo here — the profile keeps its stored answers
      // until the customer picks a new set and presses Save (which, since every
      // question is required, can't happen while the form is blank).
      onReset(event) {
        event.preventDefault();
        for (const input of this.querySelectorAll('.survey__checkbox')) {
          input.checked = false;
        }
        this.clearMissing();
        this.setMessage('');
      }

      /* ------------------------------------------------------- required rule */

      isAnswered(question) {
        return !!question.querySelector('.survey__checkbox:checked');
      }

      // Every question must be answered before we save anything.
      missingQuestions() {
        return this.questions.filter((q) => !this.isAnswered(q));
      }

      flagMissing(question, missing) {
        question.classList.toggle('survey__question--missing', missing);
        if (missing) question.setAttribute('aria-invalid', 'true');
        else question.removeAttribute('aria-invalid');
      }

      clearMissing() {
        for (const question of this.questions) this.flagMissing(question, false);
      }

      // { propertyKey: [selected values] } for every question, selected or not.
      collectAnswers() {
        const answers = {};
        for (const question of this.questions) {
          const key = question.dataset.property;
          if (!key) continue;
          answers[key] = Array.from(question.querySelectorAll('.survey__checkbox'))
            .filter(function (input) { return input.checked; })
            .map(function (input) { return input.value; });
        }
        return answers;
      }

      /* ----------------------------------------------------------- write */

      async onSubmit(event) {
        event.preventDefault();
        if (this.saving) return;

        // In preview there is deliberately no email, so skip the guard that would
        // otherwise report a broken survey to a merchant looking at a fine one.
        if (!this.preview && (!this.publicKey || (!this.email && !this.profileId))) {
          console.error('[survey] missing Klaviyo public key or profile identifier');
          this.setMessage(this.dataset.saveErrorText || 'We couldn’t save your answers.', 'error');
          return;
        }

        // All questions are required. Save nothing unless every one is answered,
        // and point at the ones that aren't rather than just refusing.
        this.clearMissing();
        const missing = this.missingQuestions();
        if (missing.length) {
          for (const question of missing) this.flagMissing(question, true);
          this.setMessage(
            this.dataset.requiredText || 'Please answer every question before saving.',
            'error'
          );
          const firstInput = missing[0].querySelector('.survey__checkbox');
          if (firstInput) {
            firstInput.focus({ preventScroll: true });
            missing[0].scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
          return;
        }

        // Placed after the required-answer check so the editor still exercises the
        // real validation, and only the network write is withheld.
        if (this.preview) {
          this.setMessage(
            this.dataset.previewText || 'Preview only — answers are not saved from the theme editor.'
          );
          return;
        }

        const answers = this.collectAnswers();
        this.saving = true;
        if (this.saveButton) this.saveButton.disabled = true;
        this.setMessage(this.dataset.savingText || 'Saving…');

        try {
          await this.save(answers);
          this.afterSave();
          // Those keys are gone from the profile now, so don't resend them.
          this.staleKeys = [];
          if (this.savedEl) this.showSaved();
          else this.setMessage(this.dataset.successText || 'Your answers have been saved.', 'success');
        } catch (e) {
          console.error('[survey]', e);
          this.setMessage(
            this.dataset.saveErrorText || 'We couldn’t save your answers. Please try again.',
            'error'
          );
        } finally {
          this.saving = false;
          if (this.saveButton) this.saveButton.disabled = false;
        }
      }

      async save(answers) {
        const properties = {};
        // Starts with the retired keys. onSubmit already refuses to save while
        // any question is unanswered, so the empty-value branch below is a
        // belt-and-braces guard rather than something that fires in practice —
        // it removes the property instead of writing "", which would otherwise
        // linger as an empty value in Klaviyo segments.
        const unset = this.staleKeys.slice();

        for (const question of this.questions) {
          const key = question.dataset.property;
          if (!key) continue;
          const values = answers[key] || [];
          if (!values.length) {
            unset.push(key);
          } else if (question.dataset.multiple === 'true') {
            properties[key] = values;
          } else {
            properties[key] = values[0];
          }

          const flags = this.flagsFor(question);
          for (const answer of Object.keys(flags)) {
            if (!flags[answer]) continue;
            if (values.indexOf(answer) !== -1) properties[flags[answer]] = 'Yes';
            else unset.push(flags[answer]);
          }
        }

        const attributes = {};
        if (this.email) attributes.email = this.email;
        // Shopify customer id -> Klaviyo external_id. Email stays the primary
        // identifier; this rides along so the profile carries the same external
        // id Klaviyo's Shopify integration uses, and survives an email change.
        // Omitted rather than sent empty when the id isn't available — a blank
        // external_id would be a meaningless identifier on the profile.
        if (this.customerId) attributes.external_id = String(this.customerId);
        if (Object.keys(properties).length) attributes.properties = properties;

        const data = { type: 'profile', attributes: attributes };
        if (this.profileId) data.id = this.profileId;
        if (unset.length) data.meta = { patch_properties: { unset: unset } };

        const res = await fetch(
          'https://a.klaviyo.com/client/profiles/?company_id=' + encodeURIComponent(this.publicKey),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', revision: this.apiRevision },
            body: JSON.stringify({ data: data }),
          }
        );
        // The client endpoint acknowledges with 202 and an empty body.
        if (!res.ok && res.status !== 202) {
          throw new Error('client/profiles -> ' + res.status);
        }
      }
    }
  );
}